"use client";

import { FormEvent, useMemo, useRef } from "react";
import { ArrowLeft, CalendarDays, Clock3, Loader2, Send } from "lucide-react";
import Link from "next/link";
import { FormError, FormSuccess } from "@/components/ui/form-alert";
import { publicSubmissionSuccessMessage } from "@/lib/public-submissions";
import { createFormSubmitStore } from "@/stores/workflow-stores";
import { useScopedStore } from "@/stores/scoped-store";

type FieldType = "text" | "email" | "tel" | "url" | "date" | "time" | "textarea";

export type SubmissionField = {
  name: string;
  label: string;
  type?: FieldType;
  required?: boolean;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  rows?: number;
  className?: string;
};

export type PublicSubmissionFormProps = {
  endpoint: string;
  title: string;
  eyebrow: string;
  description: string;
  submitLabel: string;
  backHref: string;
  backLabel: string;
  fields: SubmissionField[];
};

type ApiResponse = {
  success?: boolean;
  data?: { message?: string };
  message?: string;
};

export function PublicSubmissionForm({
  endpoint,
  title,
  eyebrow,
  description,
  submitLabel,
  backHref,
  backLabel,
  fields
}: PublicSubmissionFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const { isSubmitting, error, success, setIsSubmitting, setError, setSuccess } = useScopedStore(
    `public-submission:${endpoint}`,
    createFormSubmitStore,
    (state) => state
  );
  const requiredNote = useMemo(() => fields.some((field) => field.required), [fields]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSuccess(null);
    setError(null);
    setIsSubmitting(true);

    try {
      const formData = new FormData(event.currentTarget);
      const payload = Object.fromEntries(formData.entries());
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const body = (await response.json().catch(() => ({}))) as ApiResponse;

      if (!response.ok || !body.success) {
        setError(body.message ?? "We could not send your submission. Please check the form and try again.");
        return;
      }

      setSuccess(body.data?.message ?? publicSubmissionSuccessMessage);
      formRef.current?.reset();
    } catch {
      setError("We could not send your submission. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="mx-auto max-w-[1100px] px-4 py-10 sm:px-6 sm:py-14">
      <Link href={backHref} className="inline-flex items-center gap-2 text-[12px] font-black uppercase tracking-[0.16em] text-muted-foreground hover:text-brand">
        <ArrowLeft className="h-4 w-4" />
        {backLabel}
      </Link>

      <div className="mt-8 grid gap-8 lg:grid-cols-[0.9fr_1.4fr] lg:items-start">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-brand">{eyebrow}</p>
          <h1 className="mt-3 font-display text-4xl font-black leading-[0.98] sm:text-5xl">{title}</h1>
          <p className="mt-4 max-w-md text-sm leading-6 text-muted-foreground">{description}</p>
          {requiredNote ? <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Required fields are marked with *</p> : null}
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-5 shadow-lift sm:p-7">
          <input className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div className="grid gap-5 md:grid-cols-2">
            {fields.map((field) => (
              <SubmissionInput key={field.name} field={field} disabled={isSubmitting} />
            ))}
          </div>

          <div className="mt-6 space-y-4">
            <FormSuccess message={success} title="Submission received" />
            <FormError message={error} />
          </div>

          <div className="mt-6 flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-5 text-muted-foreground">Your details go to the NV admin team for manual review.</p>
            <button type="submit" disabled={isSubmitting} className="nv-button inline-flex min-h-11 items-center justify-center gap-2 px-5 py-3 text-xs disabled:cursor-not-allowed disabled:opacity-60">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {isSubmitting ? "Sending..." : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function SubmissionInput({ field, disabled }: { field: SubmissionField; disabled: boolean }) {
  const isNativeDateTime = field.type === "date" || field.type === "time";
  const NativeIcon = field.type === "date" ? CalendarDays : field.type === "time" ? Clock3 : null;
  const label = (
    <span className="text-xs font-black uppercase tracking-wider text-foreground/70">
      {field.label} {field.required ? <span className="text-brand">*</span> : null}
    </span>
  );
  const commonProps = {
    id: field.name,
    name: field.name,
    required: field.required,
    disabled,
    placeholder: field.placeholder,
    minLength: field.minLength,
    maxLength: field.maxLength,
    className: `nv-input ${isNativeDateTime ? "public-submission-native-input pr-10" : ""}`,
    "aria-label": field.label
  };

  if (field.type === "textarea") {
    return (
      <label className={`grid gap-1.5 ${field.className ?? ""}`}>
        {label}
        <textarea {...commonProps} rows={field.rows ?? 5} className="nv-input min-h-28 resize-y" />
      </label>
    );
  }

  return (
    <label className={`grid gap-1.5 ${field.className ?? ""}`}>
      {label}
      {NativeIcon ? (
        <span className="relative block">
          <input {...commonProps} type={field.type ?? "text"} />
          <NativeIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white" aria-hidden="true" />
        </span>
      ) : (
        <input {...commonProps} type={field.type ?? "text"} />
      )}
    </label>
  );
}
